import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AnimationEvent } from '@angular/animations';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  CARD_CONTROL_BTN,
  CARD_PURCHASE_STATUS,
  CARDS_MODE,
  Cards_Page_State,
  ICard,
  ICardAction,
  ICardFeedModificator,
  INavpanelAction,
  ModelCardRendered,
  ModelCardRenderedSelection,
  NAVPANEL_ACTION_TYPE
} from 'src/app/models/card';
import { ICardCollection } from 'src/app/models/collection';
import { FOOTER_MODE, NAVBAR_BTN } from 'src/app/models/menu';
import { ModelsCardComponent } from 'src/app/shared/components/models-card/models-card.component';
import { NavbarComponent } from 'src/app/shared/components/navbar/navbar.component';
import { cardFeedAnimation, fullscreenPanelAnimation, homeOverscrollAnimation, modelFeedAnimation, navpanelAnimation } from 'src/app/shared/providers/animations-list';
import { CardsService } from 'src/app/shared/providers/cards.service';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { SettingsService } from 'src/app/shared/providers/settings.service';
import { UserDataService } from 'src/app/shared/providers/user-data.service';
import { UtilityFunctionsService } from 'src/app/shared/providers/utility-functions.service';
import { ReviewCollectionsService } from 'src/app/shared/providers/review-collections.service';

@Component({
  selector: 'app-store',
  templateUrl: './store.component.html',
  styleUrls: ['./store.component.scss'],
  animations: [
    cardFeedAnimation,
    fullscreenPanelAnimation,
    modelFeedAnimation,
    homeOverscrollAnimation,
    navpanelAnimation
  ]
})
export class StoreComponent implements OnInit, OnDestroy {
  @ViewChild('navbarRef', { static: false })
  public navbarRef: NavbarComponent;

  @ViewChild('modelCardsFeed', { static: false })
  public modelCardsFeedRef: ModelsCardComponent;

  public CARDS_MODE = CARDS_MODE;
  public FOOTER_MODE = FOOTER_MODE;
  public currentAnimationState: string = Cards_Page_State.CARD_FEED;
  public currentFooterMode: FOOTER_MODE = FOOTER_MODE.STORE;
  public readonly storeFeedModificator: ICardFeedModificator = { isStoreFeed: true };

  public collectionDetailsSource: ICardCollection | null = null;
  public modelFeedCards: ModelCardRendered[] = [];
  public modelFeedCardStartPosition: number = 0;
  public isModelFeedShown: boolean = false;
  public isNavpanelShown: boolean = false;
  public navpanelAnimationState: string = 'visible';
  public isNavpanelHidden: boolean = false;
  public isNoDataRecieved: boolean = false;
  public errorData: unknown;
  public storeNavpanelItems: INavpanelAction[] = [];

  public isCardPublishShown: boolean = false;
  public cardSelectData: ModelCardRenderedSelection[] = [];

  private modelIdChangeSubscription: Subscription;
  private routeModelId: string | null = null;
  private isComponentActive: boolean = true;
  private activeLoadToken: number = 0;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    public cardService: CardsService,
    public reviewCollectionsService: ReviewCollectionsService,
    public settingsService: SettingsService,
    public userDataService: UserDataService,
    public utilityService: UtilityFunctionsService,
    public firebaseAuth: FirebaseAuthService
  ) {}

  async ngOnInit(): Promise<void> {
    this.settingsService.hideSplashScreen();

    this.modelIdChangeSubscription = this.route.queryParamMap.subscribe((queryParams) => {
      this.routeModelId = queryParams.get('modelId');
      this.tryOpenModelFromQueryParam();
    });

    await this.reloadPublishedReviewsCollection();
  }

  ngOnDestroy(): void {
    this.isComponentActive = false;
    this.invalidateActiveLoad();
    this.modelIdChangeSubscription?.unsubscribe();
  }

  private async reloadPublishedReviewsCollection(): Promise<void> {
    const loadToken = ++this.activeLoadToken;
    this.isNoDataRecieved = false;
    this.errorData = undefined;

    try {
      const reviewCollection = await this.reviewCollectionsService.getPublicStoreCollection();
      if (!this.isLoadTokenActive(loadToken)) return;
      this.collectionDetailsSource = reviewCollection;
      this.modelFeedCards = reviewCollection?.collectionCards || [];
      this.tryOpenModelFromQueryParam();
    } catch (error) {
      if (!this.isLoadTokenActive(loadToken)) return;
      this.isNoDataRecieved = true;
      this.errorData = error?.error?.status || error?.message || JSON.stringify(error);
      this.utilityService.rollbarError(`StoreComponent.loadPublishedReviewsCollection error: ${JSON.stringify(error)}`);
    }
  }

  private invalidateActiveLoad(): void {
    this.activeLoadToken += 1;
  }

  private isLoadTokenActive(loadToken: number): boolean {
    return this.isComponentActive && this.activeLoadToken === loadToken;
  }

  private tryOpenModelFromQueryParam(): void {
    if (!this.routeModelId || !this.collectionDetailsSource?.collectionCards?.length) return;
    const targetIndex = this.findCollectionCardIndexByModelIdentifier(this.routeModelId);
    if (targetIndex < 0) return;
    if (this.isModelFeedShown && this.modelFeedCardStartPosition === targetIndex) return;
    this.onCollectionDetailsThumbClick(targetIndex);
  }

  private findCollectionCardIndexByModelIdentifier(modelIdentifier: string): number {
    const normalizedRequestedModelIdentifier = this.normalizeModelIdentifier(modelIdentifier);
    if (!normalizedRequestedModelIdentifier) return -1;

    return this.collectionDetailsSource.collectionCards.findIndex((card) => {
      const normalizedCardId = this.normalizeModelIdentifier(card.id);
      const normalizedPublicModelId = this.normalizeModelIdentifier(card.publicModelId);

      return (
        normalizedRequestedModelIdentifier === normalizedCardId ||
        normalizedRequestedModelIdentifier === normalizedPublicModelId
      );
    });
  }

  private normalizeModelIdentifier(modelIdentifier?: string | null): string {
    if (!modelIdentifier) return '';
    return this.safeDecodeURIComponent(modelIdentifier).trim().toLowerCase();
  }

  private safeDecodeURIComponent(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private updateModelIdQueryParam(modelId: string | null): void {
    this.routeModelId = modelId;
    const queryParams: Params = modelId
      ? { modelId }
      : { modelId: null };

    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    }).catch((err) => {
      console.error('StoreComponent.updateModelIdQueryParam error:', err);
    });
  }

  public onStoreThumbClicked(card: ModelCardRendered): void {
    if (!card || !this.collectionDetailsSource?.collectionCards?.length) return;
    const thumbIndex = this.collectionDetailsSource.collectionCards.findIndex(item => item.id === card.id);
    if (thumbIndex < 0) return;
    this.updateModelIdQueryParam(card.id);
    this.onCollectionDetailsThumbClick(thumbIndex);
  }

  public onCollectionDetailsThumbClick(thumbNumber: number): void {
    if (!this.collectionDetailsSource?.collectionCards?.length) return;

    this.modelFeedCards = this.collectionDetailsSource.collectionCards;
    this.cardService.modelFeedInitStart = performance.now();
    this.modelFeedCardStartPosition = thumbNumber;
    this.currentFooterMode = FOOTER_MODE.DEFAULT;
    this.isModelFeedShown = true;
  }

  public onTopNavAdditionalMenuBtnClicked(): void {
    this.utilityService.triggerHaptics();
    this.navbarRef?.navbarBtnClick(NAVBAR_BTN.ACCOUNT);
  }

  public onNavbarBtnClick(navbarBtn: NAVBAR_BTN): void {
    switch (navbarBtn) {
      case NAVBAR_BTN.HOME:
        this.router.navigate(['/']);
        break;
      case NAVBAR_BTN.BACK:
        if (this.isCardPublishShown) {
          this.closePublishBulkMode();
          break;
        }
        this.router.navigate(['/']);
        break;
      case NAVBAR_BTN.LIBRARY:
        this.router.navigate(['/library']);
        break;
      case NAVBAR_BTN.BACK_PROFILE:
        this.currentFooterMode = FOOTER_MODE.STORE;
        break;
      case NAVBAR_BTN.PUBLISH:
        if (!this.firebaseAuth.currentUser) {
          this.navbarRef?.handleUnauthorizedUserActionDetected();
          break;
        }
        if (!this.isCardPublishShown) {
          this.openPublishBulkMode();
        } else {
          this.publishSelectedModelsBulk();
        }
        break;
      case NAVBAR_BTN.SEARCH:
        this.utilityService.showImportantSnackBar('Coming soon', 250, 750);
        break;
      default:
        break;
    }
  }

  public onFootermodeChanged(value: FOOTER_MODE): void {
    this.currentFooterMode = value;
  }

  public onNavpanelActionItemClicked(item: INavpanelAction): void {
    this.handleNavpanelAction(item);
    this.isNavpanelShown = false;
  }

  public onHideNavpanelEventEmitted(): void {
    this.isNavpanelShown = false;
  }

  public onNavpanelDestroy(): void {
    this.navpanelAnimationState = 'visible';
    this.isNavpanelHidden = false;
  }

  public onNavpanelAnimationDone(event: AnimationEvent): void {
    if(event.toState !== 'void') {
      if(this.navpanelAnimationState === 'hidden') this.isNavpanelHidden = true;
    }
  }

  public onCardCollectionControlBtnClicked(action: ICardAction): void {
    this.utilityService.triggerHaptics();

    switch (action.controlAction) {
      case CARD_CONTROL_BTN.ANONYMOUS_USER:
        this.navbarRef?.handleUnauthorizedUserActionDetected();
        break;
      case CARD_CONTROL_BTN.CONTROL_BACK_BTN:
        this.isNavpanelShown = false;
        this.currentFooterMode = FOOTER_MODE.STORE;
        this.isModelFeedShown = false;
        this.updateModelIdQueryParam(null);
        break;
      case CARD_CONTROL_BTN.CONTROL_ACTION_FOR_PUBLISHED:
      case CARD_CONTROL_BTN.CONTROL_ACTIONS_DEFAULT:
      case CARD_CONTROL_BTN.CONTROL_ACTION_FOR_LIKED:
      case CARD_CONTROL_BTN.CONTROL_ACTIONS_FOR_UNLIKED:
        this.openStoreModelMoreMenu(action.card);
        break;
      case CARD_CONTROL_BTN.SEARCH:
      case CARD_CONTROL_BTN.BUY_MODEL:
      case CARD_CONTROL_BTN.CANCEL_PUBLISH:
        this.utilityService.showImportantSnackBar('Coming soon', 250, 750);
        break;
      default:
        break;
    }
  }

  private handleNavpanelAction(action: INavpanelAction): void {
    switch (action.actionType) {
      case NAVPANEL_ACTION_TYPE.SHARE_MODEL:
        this.modelCardsFeedRef?.onShareBtnClicked(this.cardService.getCurrentCard());
        break;
      case NAVPANEL_ACTION_TYPE.UNLIKE_CARD:
        this.modelCardsFeedRef?.dispatchCurrentCardLikeClick();
        break;
      default:
        break;
    }
  }

  public onPublishBulkSelected(selectedCards: ModelCardRenderedSelection[]): void {
    this.userDataService.changeStateIsNavbarMainActionDisabledTo(selectedCards.length === 0);
  }

  private isCardAvailableForPublishBulk(card: ModelCardRenderedSelection): boolean {
    if (!card) return false;
    return card.purchaseStatus !== CARD_PURCHASE_STATUS.IN_REVIEW
      && card.purchaseStatus !== CARD_PURCHASE_STATUS.PUBLISHED
      && card.purchaseStatus !== CARD_PURCHASE_STATUS.REJECTED
      && card.purchaseStatus !== CARD_PURCHASE_STATUS.REVIEW_PUBLISH_REJECT;
  }

  private getCardsForPublishBulk(): ModelCardRenderedSelection[] {
    const reviewStateById = new Map<string, ICard>(
      this.userDataService.userReviewPublishRejectModels
        .filter((card) => !!card?.id)
        .map((card) => [card.id, card])
    );

    return (this.userDataService.uploadedCards || [])
      .filter((card) => !!card?.id)
      .map((uploadedCard) => {
        const reviewCard = reviewStateById.get(uploadedCard.id);
        const mergedCard: ModelCardRenderedSelection = { ...(uploadedCard as ModelCardRenderedSelection) };
        if (reviewCard) {
          mergedCard.purchaseStatus = reviewCard.purchaseStatus;
          mergedCard.modifiedDate = reviewCard.modifiedDate;
          mergedCard.publicModelId = reviewCard.publicModelId;
        }
        return mergedCard;
      });
  }

  private openPublishBulkMode(): void {
    this.cardSelectData = this.getCardsForPublishBulk();
    this.cardSelectData.forEach(card => {
      card.isSelected = false;
      card.isDisabled = !this.isCardAvailableForPublishBulk(card);
    });
    this.isModelFeedShown = false;
    this.isCardPublishShown = true;
    this.currentFooterMode = FOOTER_MODE.PUBLISH_SELECTED_MODELS;
    this.userDataService.changeStateIsNavbarMainActionDisabledTo(true);
  }

  private clearPublishBulkSelectionState(): void {
    this.cardSelectData?.forEach(card => {
      card.isSelected = false;
      card.isDisabled = undefined;
    });
  }

  private closePublishBulkMode(): void {
    this.clearPublishBulkSelectionState();
    this.isCardPublishShown = false;
    this.currentFooterMode = FOOTER_MODE.STORE;
    this.userDataService.changeStateIsNavbarMainActionDisabledTo(false);
  }

  private publishSelectedModelsBulk(): void {
    const selectedCardsToPublish = (this.cardSelectData || [])
      .filter(card => card.isSelected && this.isCardAvailableForPublishBulk(card));

    if (selectedCardsToPublish.length === 0) {
      this.userDataService.changeStateIsNavbarMainActionDisabledTo(true);
      return;
    }

    this.closePublishBulkMode();
    this.userDataService.sendModelsForReview(selectedCardsToPublish);
  }

  private openStoreModelMoreMenu(card?: ICard): void {
    if (!card) return;

    const navpanelItems: INavpanelAction[] = [];
    const isOwnModel = this.modelCardsFeedRef?.isOwnModel(card) === true;
    const isSaved = this.userDataService.isCardExitsInSaved(card);

    if (!isOwnModel) {
      navpanelItems.push({
        actionName: isSaved ? 'Remove from Library' : 'Add to Library',
        actionType: NAVPANEL_ACTION_TYPE.UNLIKE_CARD
      });
    }

    navpanelItems.push({
      actionName: 'Share Model',
      actionType: NAVPANEL_ACTION_TYPE.SHARE_MODEL
    });

    this.storeNavpanelItems = navpanelItems;
    this.isNavpanelShown = true;
  }
}
